// Fiche de stock d'une installation (P05 dépôt, cuves d'une station ; charte D-038, « exceptions d'abord ») :
// en tête ce qui demande une action (alertes ouvertes, anomalies, éléments en attente de validation, réservoirs
// non jaugés aujourd'hui) et l'action principale « Déclarer le jaugeage du jour » ; puis le stock par produit
// (physique, comptable, utilisable, autonomie et confiance), les réservoirs (cuves à niveau avec fond de cuve,
// hachures pour une donnée ancienne), les positions par propriétaire, les mouvements, l'historique des
// jaugeages, les anomalies et l'import. Les droits viennent du serveur ; aucun bouton sans traitement.

import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { formaterNombre, LIBELLES_ETATS } from '@carburanttrack/shared';
import { LIBELLES_SOUS_TYPES_AJUSTEMENT, LIBELLES_STATUT_PRODUIT, LIBELLES_TYPES_MOUVEMENTS, LIBELLES_MOTIF_NON_DISTRIBUABLE, LIBELLES_STATUT_STATION } from '@carburanttrack/shared/matiere';
import { api } from '../../api/client';
import { Indisponible, MessageErreur, Squelette } from '../../composants/Etats';
import { Icone } from '../../composants/Icones';
import { dateHeure, jourLong, libelleCategorie, libelleProvince } from '../../composants/format';
import { AutonomieJours, BadgeValidation, CuveSeuil, EtiquetteNiveau, PastilleFraicheur, QualiteDonneeEtiquette, quantite, TableauPositions, tonDeNiveau, useNomProduit } from './composants';
import { DecisionMotivee, FormulaireCession, FormulaireCorrection, FormulaireImport, FormulaireJaugeage, FormulaireMouvement } from './Formulaires';
import type { Declaration, FicheStock, Mouvement } from './types';

type Onglet = 'reservoirs' | 'proprietaires' | 'mouvements' | 'jaugeages' | 'anomalies' | 'import';
type Panneau = 'jaugeage' | 'mouvement' | 'cession' | null;

interface StatutStationVue {
  statutStation: string | null;
  motifStation: string | null;
  informationNonActualisee: boolean;
  fraicheur: string;
  derniereDeclaration: string | null;
  produits: { produit: string; statut: keyof typeof LIBELLES_STATUT_PRODUIT; motif: string | null; stockUtilisable: string | null; unite: string | null; age: string | null }[];
}

interface CorrectionVue {
  id: string;
  objetId: string;
  installationId: string;
  ancienneValeur: string;
  nouvelleValeur: string;
  unite: string;
  motif: string;
  etat: string;
  organisationAuteurNom: string | null;
  dateLimiteContestation: string | null;
  creeLe: string;
}

export function useFicheStock(id: string | undefined) {
  return useQuery({ queryKey: ['stock', id], queryFn: async () => (await api<FicheStock>(`/api/v1/stocks/installations/${id}`)).donnees, enabled: Boolean(id) });
}

export function PageFicheStock() {
  const { id } = useParams();
  const fiche = useFicheStock(id);
  const nomProduit = useNomProduit();
  const [onglet, setOnglet] = useState<Onglet>('reservoirs');
  // ?declarer=1 (lien « Déclarer mes cuves » de l'accueil de la station) ouvre directement le formulaire.
  const [parametres] = useSearchParams();
  const [panneau, setPanneau] = useState<Panneau>(parametres.get('declarer') ? 'jaugeage' : null);
  const [reservoirChoisi, setReservoirChoisi] = useState<string | undefined>(undefined);
  const estStation = fiche.data?.installation.categorie === 'station';
  const statut = useQuery({
    queryKey: ['stock', id, 'statut'],
    queryFn: async () => (await api<StatutStationVue>(`/api/v1/stations/${id}/statut`)).donnees,
    enabled: Boolean(id && estStation),
  });
  const corrections = useQuery({
    queryKey: ['stock', id, 'corrections'],
    queryFn: async () => (await api<CorrectionVue[]>('/api/v1/corrections?etat=proposee')).donnees.filter((c) => c.installationId === id),
    enabled: Boolean(fiche.data?.comptabiliteMatiere),
    retry: false,
  });

  if (fiche.isLoading) return <Squelette lignes={10} libelle="Chargement de la fiche de stock" />;
  if (fiche.error) return <MessageErreur erreur={fiche.error} titre="Fiche de stock indisponible" />;
  const f = fiche.data!;
  const i = f.installation;
  const sansMesure = f.reservoirs.filter((r) => f.reservoirsSansMesureDuJour.includes(r.id));
  const ouvrir = (p: Panneau, reservoir?: string) => {
    setReservoirChoisi(reservoir);
    setPanneau(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const nbExceptions = f.alertesOuvertes.length + f.anomalies.length + f.declarationsEnAttente.length + f.mouvementsEnAttente.length + (corrections.data?.length ?? 0) + (f.physiqueLisible && f.droits.declarer ? sansMesure.length : 0);
  const onglets: { cle: Onglet; libelle: string; visible: boolean; compte?: number }[] = [
    { cle: 'reservoirs', libelle: estStation ? 'Cuves' : 'Réservoirs', visible: f.physiqueLisible },
    { cle: 'proprietaires', libelle: 'Stocks par propriétaire', visible: f.comptabiliteMatiere, compte: f.proprietaires.length },
    { cle: 'mouvements', libelle: 'Mouvements', visible: f.comptabiliteMatiere || estStation },
    { cle: 'jaugeages', libelle: estStation ? 'Historique des déclarations' : 'Historique des jaugeages', visible: f.physiqueLisible },
    { cle: 'anomalies', libelle: 'Anomalies', visible: f.physiqueLisible, compte: f.anomalies.length },
    { cle: 'import', libelle: 'Import', visible: f.droits.importer },
  ];
  const ongletCourant = onglets.find((o) => o.cle === onglet && o.visible) ?? onglets.find((o) => o.visible)!;
  const rang = { critique: 0, elevee: 1, preventive: 2, informative: 3 } as const;
  const tonProduit = (code: string) => tonDeNiveau(f.alertesOuvertes.filter((a) => a.produitCode === code).sort((a, b) => rang[a.niveau] - rang[b.niveau])[0]?.niveau);

  return (
    <div className="page-fiche-stock">
      <div className="page-tete">
        <div>
          <span className="contexte">
            {i.organisationNom}, {i.provinceNom ?? libelleProvince(i.provinceCode)}, {jourLong()}
          </span>
          <h1>{i.nom}</h1>
          <p>
            {libelleCategorie(i.categorie)}
            {f.comptabiliteMatiere ? `, tolérance d’écart physique contre comptable ${formaterNombre(String(f.tolerancePourcentage), 1)} %` : ''}
          </p>
        </div>
        <div className="actions">
          {f.droits.declarer && f.physiqueLisible && (
            <button type="button" className="bouton primaire" onClick={() => ouvrir('jaugeage', sansMesure[0]?.id)}>
              <Icone nom="formulaire" />
              {estStation ? 'Déclarer mes cuves' : 'Déclarer le jaugeage du jour'}
            </button>
          )}
          {f.droits.mouvement && (
            <button type="button" className="bouton" onClick={() => ouvrir('mouvement')}>
              Enregistrer un mouvement
            </button>
          )}
          {f.droits.cession && (
            <button type="button" className="bouton" onClick={() => ouvrir('cession')}>
              Céder une position
            </button>
          )}
          <Link className="bouton discret" to={`/referentiel/installations/${i.id}`}>
            Fiche de l’installation
          </Link>
        </div>
      </div>

      {panneau === 'jaugeage' && f.droits.declarer && f.physiqueLisible && <FormulaireJaugeage fiche={f} reservoirInitial={reservoirChoisi} fermer={() => setPanneau(null)} />}
      {panneau === 'mouvement' && <FormulaireMouvement fiche={f} fermer={() => setPanneau(null)} />}
      {panneau === 'cession' && <FormulaireCession fiche={f} fermer={() => setPanneau(null)} />}

      {!f.physiqueLisible && f.motifPhysique && (
        <div className="bloc-insuffisant">
          <b>Stock physique du dépôt</b>
          <span>
            <em>indisponible</em> : {f.motifPhysique.charAt(0).toLowerCase() + f.motifPhysique.slice(1)}.
          </span>
        </div>
      )}

      {nbExceptions > 0 ? (
        <section aria-labelledby="titre-a-traiter" className="espace-bas">
          <h2 id="titre-a-traiter" className="sur-titre">
            À traiter d’abord
          </h2>
          <ol className="liste-exceptions">
            {f.alertesOuvertes.map((a) => (
              <li key={a.id}>
                <Link className={`exception ${tonDeNiveau(a.niveau)}`} to={`/alertes/${a.id}`}>
                  <span className="numero" aria-hidden="true">
                    <Icone nom="incident" taille={16} />
                  </span>
                  <span className="texte">
                    <span className="marque-exception">
                      <EtiquetteNiveau niveau={a.niveau} />
                    </span>
                    <b>{a.titre}</b>
                    <small>Détectée le {dateHeure(a.derniereDetection)}</small>
                  </span>
                  <Icone nom="chevron" />
                </Link>
              </li>
            ))}
            {f.anomalies.map((a) => (
              <li key={a.id}>
                <button type="button" className="exception ambre bouton-exception" onClick={() => setOnglet('anomalies')}>
                  <span className="numero" aria-hidden="true">
                    !
                  </span>
                  <span className="texte">
                    <span className="marque-exception">{a.typeLibelle}</span>
                    <b>{a.libelle}</b>
                    <small>Ouverte le {dateHeure(a.creeLe)}, à vérifier</small>
                  </span>
                  <Icone nom="chevron" />
                </button>
              </li>
            ))}
            {f.physiqueLisible &&
              f.droits.declarer &&
              sansMesure.map((r) => (
                <li key={r.id}>
                  <button type="button" className="exception insuffisante bouton-exception" onClick={() => ouvrir('jaugeage', r.id)}>
                    <span className="numero" aria-hidden="true">
                      <Icone nom="cuve" taille={16} />
                    </span>
                    <span className="texte">
                      <span className="marque-exception">Jaugeage du jour</span>
                      <b>
                        {r.numero}, {nomProduit(r.produitCode)} : pas encore jaugé aujourd’hui
                      </b>
                      <small>{r.horodatagePhysique ? `Dernière mesure ${r.age}` : 'Aucune mesure enregistrée'}</small>
                    </span>
                    <Icone nom="chevron" />
                  </button>
                </li>
              ))}
          </ol>
          {f.declarationsEnAttente.length > 0 && (
            <div className="carte-bloc espace-haut">
              <h3>Déclarations en attente de validation</h3>
              {f.declarationsEnAttente.map((d) => (
                <div key={d.id} className="element-attente">
                  <p>
                    <b>{f.reservoirs.find((r) => r.id === d.reservoirId)?.numero ?? 'Réservoir'}</b>, {quantite(d.volumeBrut, d.unite)} mesuré le {dateHeure(d.horodatageMesure)}
                  </p>
                  <p className="attenue">{d.motifs.join(' ; ') || 'Validation systématique demandée par le circuit de l’organisation'}</p>
                  {f.droits.validerDeclarations && <DecisionMotivee url={`/api/v1/stocks/declarations/${d.id}/decision`} libelleValider="Valider" libelleRejeter="Rejeter" cles={['stock', 'stocks']} />}
                </div>
              ))}
            </div>
          )}
          {f.mouvementsEnAttente.length > 0 && (
            <div className="carte-bloc espace-haut">
              <h3>Mouvements en attente de validation</h3>
              {f.mouvementsEnAttente.map((m) => (
                <div key={m.id} className="element-attente">
                  <p>
                    <b>{LIBELLES_TYPES_MOUVEMENTS[m.type as keyof typeof LIBELLES_TYPES_MOUVEMENTS] ?? m.type}</b> ({m.sens === 'entree' ? 'entrée' : 'sortie'}) de {quantite(m.quantite, m.unite)}, {nomProduit(m.produitCode)}, {m.proprietaireNom}, le {dateHeure(m.horodatageEvenement)}
                  </p>
                  {f.droits.validerMouvements && <DecisionMotivee url={`/api/v1/stocks/mouvements/${m.id}/decision`} libelleValider="Valider" libelleRejeter="Rejeter" cles={['stock', 'stocks']} />}
                </div>
              ))}
            </div>
          )}
          {(corrections.data?.length ?? 0) > 0 && (
            <div className="carte-bloc espace-haut">
              <h3>Corrections proposées, à valider par un second agent</h3>
              {corrections.data!.map((c) => (
                <div key={c.id} className="element-attente">
                  <p>
                    Quantité corrigée de {quantite(c.ancienneValeur, c.unite)} à {quantite(c.nouvelleValeur, c.unite)} : {c.motif}
                  </p>
                  <DecisionMotivee url={`/api/v1/corrections/${c.id}/decision`} libelleValider="Valider la correction" libelleRejeter="Refuser" valeurs={['valider', 'refuser']} cles={['stock', 'stocks']} />
                </div>
              ))}
            </div>
          )}
        </section>
      ) : (
        <p className="message info">Rien à traiter pour cette installation : les mesures du jour sont enregistrées et aucune anomalie n’est ouverte.</p>
      )}

      {estStation && statut.data && (
        <section className="carte-bloc" aria-labelledby="titre-statut">
          <div className="carte-bloc-tete">
            <h2 id="titre-statut">Statut déclaré</h2>
            <span className="attenue">
              {statut.data.informationNonActualisee
                ? 'Information non actualisée : dernière déclaration périmée'
                : `${statut.data.statutStation ? LIBELLES_STATUT_STATION[statut.data.statutStation as keyof typeof LIBELLES_STATUT_STATION] : 'indisponible'}, déclaré le ${dateHeure(statut.data.derniereDeclaration)}`}
            </span>
          </div>
          <ul className="puces">
            {statut.data.produits.map((p) => (
              <li key={p.produit} className={`etiquette${p.statut === 'rupture_confirmee' ? ' rouge' : p.statut === 'non_distribuable' || p.statut === 'information_non_actualisee' ? ' ambre' : ''}`}>
                {nomProduit(p.produit)} : {LIBELLES_STATUT_PRODUIT[p.statut]}
                {p.motif ? `, ${LIBELLES_MOTIF_NON_DISTRIBUABLE[p.motif as keyof typeof LIBELLES_MOTIF_NON_DISTRIBUABLE] ?? p.motif.charAt(0).toLowerCase() + p.motif.slice(1)}` : ''}
              </li>
            ))}
          </ul>
        </section>
      )}

      {f.physiqueLisible && (
        <section aria-labelledby="titre-produits">
          <h2 id="titre-produits" className="sur-titre espace-bas-petit">
            Stock par produit
          </h2>
          <div className="grille-produits">
            {f.produits.map((p) => {
              const ton = tonProduit(p.produitCode);
              return (
                <div key={p.produitCode} className={`carte-produit${p.physique === null || p.fraicheur === 'perimee' ? ' insuffisante' : ton ? ` ${ton}` : ''}`}>
                  <span className="nom">{p.produitNom}</span>
                  {p.physique === null ? <Indisponible motif={p.motifPhysique ?? 'aucune mesure'} /> : <span className="grand">{quantite(p.physique, p.unite)}</span>}
                  <small>
                    {p.derniereMesure ? `Mesure la plus ancienne du produit : ${dateHeure(p.derniereMesure)}` : 'Aucune mesure'}
                    {p.comptable !== null ? `, comptable ${quantite(p.comptable, p.unite, p.physique)}` : ''}
                  </small>
                  <small>Utilisable : {p.utilisable === null ? 'indisponible (mesure absente ou périmée)' : quantite(p.utilisable, p.unite, p.physique)}</small>
                  {p.autonomie ? <AutonomieJours jours={p.autonomie.jours} confiance={p.autonomie.confiance} motif={p.autonomie.motif} joursAvecLivraisons={p.autonomie.joursAvecLivraisons} ton={ton} /> : <Indisponible motif="autonomie non encore calculée" />}
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div className="onglets" role="tablist" aria-label="Détail du stock">
        {onglets
          .filter((o) => o.visible)
          .map((o) => (
            <button key={o.cle} type="button" role="tab" id={`onglet-${o.cle}`} aria-selected={ongletCourant.cle === o.cle} aria-controls="panneau-stock" onClick={() => setOnglet(o.cle)}>
              {o.libelle}
              {o.compte !== undefined && o.compte > 0 ? ` (${o.compte})` : ''}
            </button>
          ))}
      </div>
      <div id="panneau-stock" role="tabpanel" aria-labelledby={`onglet-${ongletCourant.cle}`}>
        {ongletCourant.cle === 'reservoirs' && (
          <section className="carte-bloc">
            <div className="cuves compactes">
              {f.reservoirs.map((r) => (
                <CuveSeuil
                  key={r.id}
                  libelle={`${r.numero}, ${nomProduit(r.produitCode)}`}
                  capacite={r.capacite}
                  unite={r.unite}
                  volume={r.physique}
                  seuil={r.nonPompable}
                  fraicheur={r.fraicheur}
                  age={r.age}
                  motifIndisponible={r.motifPhysique ?? undefined}
                  ton={r.utilisable !== null && Number(r.utilisable) <= 0 ? 'rouge' : null}
                  mention={
                    <>
                      {r.depasseTolerance && <span className="etiquette ambre mention-ecart">Écart {r.ecartPourcentage ? `${formaterNombre(r.ecartPourcentage, 2)} %` : ''} à vérifier</span>}
                      {r.comptable !== null && <small>Comptable {quantite(r.comptable, r.unite)}</small>}
                      {f.droits.declarer && (
                        <button type="button" className="bouton discret" onClick={() => ouvrir('jaugeage', r.id)}>
                          Déclarer
                        </button>
                      )}
                    </>
                  }
                />
              ))}
            </div>
            <div className="legende-hachure">
              <span className="echantillon" aria-hidden="true" />
              Hachuré : donnée ancienne, périmée ou absente. Ce n’est pas une gravité. Le pointillé marque le fond de cuve (volume non pompable).
            </div>
          </section>
        )}
        {ongletCourant.cle === 'proprietaires' && (
          <section className="carte-bloc">
            {f.proprietaires.length === 0 ? <p className="attenue">Aucune position par propriétaire lisible dans ce dépôt.</p> : <TableauPositions positions={f.proprietaires} legende="Stocks par propriétaire" />}
            <p className="legende-tableau">La somme des positions par propriétaire égale le stock comptable des réservoirs ; tout écart ouvre une anomalie.</p>
          </section>
        )}
        {ongletCourant.cle === 'mouvements' && <OngletMouvements fiche={f} />}
        {ongletCourant.cle === 'jaugeages' && <OngletJaugeages fiche={f} />}
        {ongletCourant.cle === 'anomalies' && (
          <section className="carte-bloc">
            {f.anomalies.length === 0 ? (
              <p className="attenue">Aucune anomalie ouverte.</p>
            ) : (
              f.anomalies.map((a) => (
                <div key={a.id} className="element-attente">
                  <p>
                    <span className="etiquette ambre">{a.typeLibelle}</span> {a.libelle}
                  </p>
                  <p className="attenue">
                    Ouverte le {dateHeure(a.creeLe)}, état : {a.etat === 'en_verification' ? 'en vérification' : 'ouverte'}
                  </p>
                  {f.droits.traiterAnomalies && <DecisionMotivee url={`/api/v1/anomalies/${a.id}/cloture`} libelleValider="Clore avec ce motif" champDecision="" cles={['stock']} />}
                </div>
              ))
            )}
          </section>
        )}
        {ongletCourant.cle === 'import' && <FormulaireImport fiche={f} />}
      </div>
    </div>
  );
}

function OngletMouvements({ fiche }: { fiche: FicheStock }) {
  const nomProduit = useNomProduit();
  const [corriger, setCorriger] = useState<string | null>(null);
  const liste = useQuery({
    queryKey: ['stock', fiche.installation.id, 'mouvements'],
    queryFn: async () => (await api<Mouvement[]>(`/api/v1/stocks/installations/${fiche.installation.id}/mouvements?limite=150`)).donnees,
  });
  if (liste.isLoading) return <Squelette lignes={6} />;
  if (liste.error) return <MessageErreur erreur={liste.error} titre="Mouvements indisponibles" />;
  const choisi = liste.data!.find((m) => m.id === corriger);
  return (
    <section className="carte-bloc">
      {choisi && <FormulaireCorrection mouvementId={choisi.id} quantiteActuelle={choisi.quantite} unite={choisi.unite} installationId={fiche.installation.id} fermer={() => setCorriger(null)} />}
      {liste.data!.length === 0 ? (
        <p className="attenue">Aucun mouvement lisible.</p>
      ) : (
        <div className="tableau-conteneur">
          <table className="tableau">
            <caption className="visuellement-cache">Mouvements de l’installation</caption>
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Mouvement</th>
                <th scope="col" className="masquable">
                  Réservoir
                </th>
                <th scope="col" className="nombre">
                  Quantité
                </th>
                <th scope="col" className="masquable">
                  Propriétaire
                </th>
                <th scope="col">État</th>
                {fiche.droits.mouvement && (
                  <th scope="col">
                    <span className="visuellement-cache">Actions</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {liste.data!.map((m) => (
                <tr key={m.id}>
                  <td>{dateHeure(m.horodatageEvenement)}</td>
                  <td>
                    {LIBELLES_TYPES_MOUVEMENTS[m.type as keyof typeof LIBELLES_TYPES_MOUVEMENTS] ?? m.type}
                    {m.sousType ? `, ${LIBELLES_SOUS_TYPES_AJUSTEMENT[m.sousType as keyof typeof LIBELLES_SOUS_TYPES_AJUSTEMENT] ?? m.sousType}` : ''}
                    <small>
                      {m.sens === 'entree' ? 'Entrée' : 'Sortie'}, {nomProduit(m.produitCode)}
                      {m.contrepartieNom ? `, contrepartie ${m.contrepartieNom}` : ''}
                      {m.motif ? `, ${m.motif}` : ''}
                    </small>
                  </td>
                  <td className="masquable">{fiche.reservoirs.find((r) => r.id === m.reservoirId)?.numero ?? (m.type === 'cession' ? 'sans réservoir (cession)' : 'indisponible')}</td>
                  <td className="nombre">
                    {m.sens === 'sortie' ? '−' : '+'}
                    {quantite(m.quantite, m.unite)}
                  </td>
                  <td className="masquable">{m.proprietaireNom}</td>
                  <td>
                    <BadgeValidation etat={m.etatValidation} />
                  </td>
                  {fiche.droits.mouvement && (
                    <td>
                      {m.etatValidation === 'valide' && m.type !== 'cession' && (
                        <button type="button" className="bouton discret" onClick={() => setCorriger(m.id)}>
                          Corriger
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function OngletJaugeages({ fiche }: { fiche: FicheStock }) {
  const nomProduit = useNomProduit();
  const [reservoirId, setReservoirId] = useState('');
  const liste = useQuery({
    queryKey: ['stock', fiche.installation.id, 'declarations', reservoirId],
    queryFn: async () => (await api<Declaration[]>(`/api/v1/stocks/installations/${fiche.installation.id}/declarations?limite=120${reservoirId ? `&reservoirId=${reservoirId}` : ''}`)).donnees,
  });
  return (
    <section className="carte-bloc">
      <div className="barre-filtres">
        <div className="champ">
          <label htmlFor="h-reservoir">Réservoir</label>
          <select id="h-reservoir" value={reservoirId} onChange={(e) => setReservoirId(e.target.value)}>
            <option value="">Tous</option>
            {fiche.reservoirs.map((r) => (
              <option key={r.id} value={r.id}>
                {r.numero}, {nomProduit(r.produitCode)}
              </option>
            ))}
          </select>
        </div>
      </div>
      {liste.isLoading && <Squelette lignes={6} />}
      {liste.error && <MessageErreur erreur={liste.error} />}
      {liste.data && (
        <div className="tableau-conteneur">
          <table className="tableau">
            <caption className="visuellement-cache">Historique des déclarations de stock</caption>
            <thead>
              <tr>
                <th scope="col">Mesure</th>
                <th scope="col">Réservoir</th>
                <th scope="col" className="nombre">
                  Volume brut
                </th>
                <th scope="col" className="nombre masquable">
                  Corrigé à 15 °C
                </th>
                <th scope="col">Qualité</th>
                <th scope="col" className="masquable">
                  État
                </th>
              </tr>
            </thead>
            <tbody>
              {liste.data.map((d) => {
                const r = fiche.reservoirs.find((x) => x.id === d.reservoirId);
                return (
                  <tr key={d.id} className={d.etatValidation === 'en_attente_validation' ? 'exception-ligne' : undefined}>
                    <td>
                      {dateHeure(d.horodatageMesure)}
                      <small>{d.source === 'import' ? 'Import' : d.source === 'hors_ligne' ? 'Hors connexion' : 'Saisie'}</small>
                    </td>
                    <td>{r ? `${r.numero}, ${nomProduit(r.produitCode)}` : 'indisponible'}</td>
                    <td className="nombre">{quantite(d.volumeBrut, d.unite)}</td>
                    <td className="nombre masquable">{d.volumeCorrige ? quantite(d.volumeCorrige, d.unite) : <span className="cellule-indisponible">{d.controles.volumeCorrige?.motif ?? 'température ou densité non fournie'}</span>}</td>
                    <td>
                      <QualiteDonneeEtiquette qualite={d.qualite} />
                    </td>
                    <td className="masquable">{LIBELLES_ETATS[d.etatValidation as keyof typeof LIBELLES_ETATS] ?? d.etatValidation}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="legende-tableau">Le volume corrigé à 15 °C est un indicateur technique non réglementaire, affiché séparément du volume brut ; les agrégats restent sur le brut (D-025).</p>
      <PastilleUsage seuils={fiche.seuilsFraicheur} />
    </section>
  );
}

/** Rappel de lecture de la pastille de fraîcheur. */
function PastilleUsage({ seuils }: { seuils: FicheStock['seuilsFraicheur'] }) {
  return (
    <p className="legende-tableau">
      Fraîcheur : <PastilleFraicheur fraicheur="recente" /> moins de {seuils.recenteHeures} h, <PastilleFraicheur fraicheur="ancienne" /> de {seuils.recenteHeures} à {seuils.ancienneHeures} h, <PastilleFraicheur fraicheur="perimee" /> au-delà (seuils paramétrés, D-022).
    </p>
  );
}
